import {
  useOrganizationUsersRead,
  useOrganizationRolesRead,
  useOrganizationAuditList,
  type UserSurfaceDto,
  type RoleSurfaceDto,
  type AuditLogSurfaceDto,
} from '../../api/generated/client';
import type { AuditLogFilters } from '../../state/cache';
import {
  selectApiData,
  type ApiSuccessEnvelope,
} from '../../api/select-api-data';

/**
 * Web-only adapters retained for dedicated detail and audit endpoints that have
 * not yet moved to the generic resource data API. Descriptor structure and
 * authorization remain backend-owned.
 */
export type User = UserSurfaceDto & { permissions?: string[] };
export type Role = RoleSurfaceDto;
export type AuditLog = AuditLogSurfaceDto;

export function useUser(id: string | undefined) {
  return useOrganizationUsersRead(id ?? '', {
    query: {
      enabled: Boolean(id),
      select: (response: unknown) =>
        selectApiData(response as ApiSuccessEnvelope<UserSurfaceDto>),
    },
  });
}

export function useRole(id: string | undefined) {
  return useOrganizationRolesRead(id ?? '', {
    query: {
      enabled: Boolean(id),
      select: (response: unknown) =>
        selectApiData(response as ApiSuccessEnvelope<RoleSurfaceDto>),
    },
  });
}

export interface AuditLogPageData extends Array<AuditLog> {
  page: number;
  totalPages: number;
  total: number;
}

export function useAuditLog(filters: AuditLogFilters) {
  const params = {
    entityType: filters.entityType,
    action: filters.action,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    search: filters.search,
    page: filters.page ? String(filters.page) : undefined,
    limit: filters.limit ? String(filters.limit) : undefined,
  };
  return useOrganizationAuditList(params, {
    query: {
      select: (response: unknown) => {
        const dto = selectApiData(
          response as ApiSuccessEnvelope<{
            data: AuditLogSurfaceDto[];
            page: number;
            totalPages: number;
            total: number;
          }>,
        );
        return Object.assign(dto.data, {
          page: dto.page,
          totalPages: dto.totalPages,
          total: dto.total,
        }) as AuditLogPageData;
      },
    },
  });
}
